import { useCallback, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Upload, X, Star, Image as ImageIcon, Trash2, Eye } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

interface AssetImageUploadProps {
  assetId: string;
  handleIsPrimaryAssetImage: (imageUrl: string) => void;
}

interface LocalImage {
  id: string;
  file_name: string;
  image_s3_key: string;
  file_size: number;
  is_primary: number;
}

export function AssetImageUpload({ assetId, handleIsPrimaryAssetImage }: AssetImageUploadProps) {
  // Use local storage for non-UUID asset IDs (store-generated IDs)
  const [images, setImages] = useState<LocalImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Load images from localStorage on mount
  useEffect(() => {
    getAssetImages();
  }, [assetId]);

  const getAssetImages = async () => {
    const response: any = await apiService.get(`/purchase-order/get-asset-images/${assetId}`);

    setImages(response.data);
    const image: any = response.data.find((img: any) => img.is_primary === 1);
    if (image) {
      handleIsPrimaryAssetImage(image.image_s3_key);
    }
  }

  // Save images to localStorage when they change
  // const saveImages = useCallback((newImages: LocalImage[]) => {
  //   setImages(newImages);
  //   localStorage.setItem(`asset_images_${assetId}`, JSON.stringify(newImages));
  // }, [assetId]);



  const uploadImage = useCallback(
    async (file: File) => {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }

      setUploading(true);

      const formData = new FormData();
      formData.append("files", file);
      formData.append("individualassetId", assetId);

      const response: any = await apiService.post(
        "/purchase-order/asset-image-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
            }
          }
        }
      );
      getAssetImages();
      setUploading(false);
    },
    [assetId, images]
  );


  const deleteImage = useCallback(async (imageId: string) => {

    try {
      const response: any = await apiService.post(`/purchase-order/delete-asset-image/${imageId}`);

      getAssetImages();
      toast.success("Image deleted successfully")
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete image")
    }
  }, []);

  const setPrimaryImage = useCallback(async (imageId: string) => {
    try {
      const data = {
        imageId: imageId,
        assetId: assetId,
      }
      const response: any = await apiService.post(`/purchase-order/set-primary-image`, data);

      getAssetImages();
      toast.success("Primary image set successfully")
      const image = images.find(img => img.id === imageId);
      if (image) {
        handleIsPrimaryAssetImage(image.image_s3_key);
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to set primary image")
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    for (const file of imageFiles) {
      await uploadImage(file);
    }
  }, [uploadImage]);

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    for (const file of files) {
      await uploadImage(file);   // send one file at a time
    }

    e.target.value = '';
  };


  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Asset Images
            </span>
            <Badge variant="secondary" className="bg-[#3f8b98] text-white hover:bg-[#3f8b98]/90 border-none rounded-full h-6 w-6 flex items-center justify-center p-0 text-[11px] font-semibold">
              {images.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload Area */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              dragActive ? "border-primary bg-primary/5" : "border-border",
              uploading && "opacity-50 pointer-events-none"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="asset-image-upload"
              className="hidden"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic"
              multiple
              onChange={handleFileInput}
              disabled={uploading}
            />

            <div className="flex flex-col items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Upload className="h-6 w-6 text-primary" />
              </div>

              <div>
                <p className="text-sm font-medium mb-1">
                  {dragActive ? "Drop images here" : "Drag & drop images here"}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  or click to browse (Max 5MB per image)
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('asset-image-upload')?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Select Images
              </Button>

              {uploading && (
                <div className="w-full max-w-xs mt-2">
                  <Progress value={undefined} className="h-1" />
                  <p className="text-xs text-muted-foreground mt-1">Uploading...</p>
                </div>
              )}
            </div>
          </div>

          {/* Image Grid */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading images...
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">No images uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="relative group rounded-md overflow-hidden border bg-muted/30 hover:border-primary/50 transition-colors"
                >
                  {/* Small Thumbnail */}
                  <div className="aspect-square w-full h-16">
                    <img
                      src={image.image_s3_key}
                      alt={image.file_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Primary Indicator */}
                  {image.is_primary && (
                    <div className="absolute top-1 left-1">
                      <Star className="h-3 w-3 text-yellow-500 fill-current" />
                    </div>
                  )}

                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-white hover:bg-white/20"
                      onClick={() => setSelectedImage(image.image_s3_key)}
                      title="View image"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                    {!image.is_primary && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-white hover:bg-white/20"
                        onClick={() => setPrimaryImage(image.id)}
                        title="Set as primary"
                      >
                        <Star className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-white hover:bg-red-500/50"
                      onClick={() => setDeleteImageId(image.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
            <p>• Supported formats: JPEG, PNG, WEBP, HEIC</p>
            <p>• Maximum file size: 5MB per image</p>
            <p>• Click on an image to view full size</p>
            <p>• Set a primary image to use as thumbnail</p>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteImageId} onOpenChange={() => setDeleteImageId(null)}>
        <AlertDialogContent className='bg-white'>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteImageId) {
                  deleteImage(deleteImageId);
                  setDeleteImageId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Viewer Dialog */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setSelectedImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={selectedImage}
            alt="Full size preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
